/**
 * The keyboard shortcuts a web page is allowed to refuse, and the ones it is
 * not.
 *
 * Worth stating exactly, because a centre will rely on the answer.
 *
 * A page can cancel a keystroke the browser hands it. It is handed most
 * combinations that would print, save, view source or search the page, and
 * cancelling those closes real gaps: browser find in a Reading passage is an
 * advantage the paper-based exam does not give, and printing a paper takes it
 * out of the room entirely.
 *
 * A page is NOT handed the ones the operating system or the browser reserves.
 * Alt+F4 is a Windows window-manager command that closes the window before any
 * page sees it. Alt+Tab, the Windows key, Ctrl+Alt+Del and Chrome's own
 * Ctrl+T, Ctrl+N and Ctrl+W are the same: reserved, uncancellable, and a
 * browser that let a page keep them would be a browser nobody could escape.
 * Some are attempted below anyway — the attempt costs a line and works in some
 * browsers — but nothing here should be described to a centre as preventing
 * them. What handles those is the lockdown that records every departure and
 * ends the sitting on the second, and a machine locked down outside the
 * browser.
 *
 * Deliberately NOT blocked: cut, copy, paste, select-all and undo. The real
 * computer-delivered test offers all of them in the Writing answer, and a
 * candidate rewriting a paragraph uses them.
 */
export interface Keystroke {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

/** Ctrl on Windows and Linux, Command on a Mac — the same shortcuts either way. */
const withModifier = (e: Keystroke) => e.ctrlKey || e.metaKey;

/** Print, save, view source, and browser find. */
const BLOCKED_WITH_MODIFIER = new Set(['p', 's', 'u', 'f', 'g', 'l', 'o']);

/** Attempted, though most browsers keep these for themselves. */
const DEVTOOLS = new Set(['i', 'j', 'c']);

export function shouldBlock(e: Keystroke, options: { inFullscreen: boolean }): boolean {
  const key = e.key.toLowerCase();

  // Ctrl/Cmd+P, +S, +U, +F, +G, +L, +O. The address bar ones (L, O) are
  // usually the browser's, and cost nothing to ask for.
  if (withModifier(e) && !e.shiftKey && !e.altKey && BLOCKED_WITH_MODIFIER.has(key)) return true;

  // Ctrl+Shift+I / J / C, the developer tools. Chrome reserves these; Firefox
  // and some builds do hand them over.
  if (withModifier(e) && e.shiftKey && DEVTOOLS.has(key)) return true;

  // F12, the other way into developer tools.
  if (key === 'f12') return true;

  /**
   * F3, which is find on Windows exactly as Ctrl+F is — and Shift+F3, which is
   * find backwards. Easy to miss, because on a Mac F3 does nothing at all and
   * the person testing is usually on a Mac.
   */
  if (key === 'f3') return true;

  // Alt+D focuses the address bar in every desktop browser. It is one of the
  // few chrome shortcuts a page is often handed, so it is worth asking for.
  if (e.altKey && key === 'd') return true;

  /**
   * F11 toggles full screen. Refused only while the exam has the screen: out
   * of full screen it is one of the ways back in, and the candidate is
   * already being shown a button that does the same thing.
   */
  if (key === 'f11' && options.inFullscreen) return true;

  return false;
}
